import { create } from 'zustand';
import { Site, Tower, TowerEquipment, MountingPoint, TowerShape, StayWire } from '../types/site';
import { Equipment } from '../types';
import { useSitesStore, generateSiteId as generateId } from './sitesStore';
import { useAuthStore } from './authStore';
import { patchAndPersistAccountUiPrefs } from '../utils/accountUiPrefs';
import {
  calculateMountingPoints,
  normDegrees as norm,
  applyOrientationToMountingPoints,
  applyOrientationToStayWires,
} from '../lib/towerUtils';

const STORAGE_KEYS = {
  SITES: 'netprobuilder_sites',
  LAST_SITE: 'netprobuilder_last_site',
  SETTINGS: 'netprobuilder_site_settings'
};

const persistSite = async (site: Site) => {
  try {
    await useSitesStore.getState().saveSite(site);
  } catch (error) {
    console.error('Failed to save site to Supabase:', error);
  }
};

const loadSiteFromStorage = (siteId: string): Site | null => {
  try {
    const sites = JSON.parse(localStorage.getItem(STORAGE_KEYS.SITES) || '{}');
    const siteData = sites[siteId];
    if (siteData) {
      // Ensure sectionHeight exists for legacy sites
      if (!siteData.tower?.sectionHeight) {
        siteData.tower = { ...siteData.tower, sectionHeight: 3 };
      }
      // Ensure shape exists for legacy sites
      if (!siteData.tower?.shape) {
        siteData.tower = { ...siteData.tower, shape: 'square' };
      }
      // Ensure stayWires exists for legacy sites
      if (!siteData.tower?.stayWires) {
        siteData.tower = { ...siteData.tower, stayWires: [] };
      }
      if (siteData.tower?.orientationDeg === undefined) {
        siteData.tower.orientationDeg = 0;
      }
      return {
        ...siteData,
        createdAt: new Date(siteData.createdAt),
        updatedAt: new Date(siteData.updatedAt)
      };
    }
  } catch (error) {
    console.error('Failed to load site:', error);
  }
  return null;
};

const getLastSiteId = (): string | null => {
  return localStorage.getItem(STORAGE_KEYS.LAST_SITE);
};

const getAllSites = (): Site[] => {
  try {
    const sites = JSON.parse(localStorage.getItem(STORAGE_KEYS.SITES) || '{}');
    return Object.values(sites).map((siteData: any) => {
      // Ensure sectionHeight exists for legacy sites
      if (!siteData.tower?.sectionHeight) {
        siteData.tower.sectionHeight = 3;
      }
      // Ensure shape exists for legacy sites
      if (!siteData.tower?.shape) {
        siteData.tower.shape = 'square';
      }
      // Ensure stayWires exists for legacy sites
      if (!siteData.tower?.stayWires) {
        siteData.tower.stayWires = [];
      }
      if (siteData.tower?.orientationDeg === undefined) {
        siteData.tower.orientationDeg = 0;
      }
      return {
        ...siteData,
        createdAt: new Date(siteData.createdAt),
        updatedAt: new Date(siteData.updatedAt)
      };
    });
  } catch (error) {
    console.error('Failed to load sites:', error);
    return [];
  }
};

interface SiteSettings {
  showTowerLabels: boolean;
}

const saveSettingsToStorage = (settings: SiteSettings) => {
  try {
    localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
  } catch (error) {
    console.error('Failed to save settings:', error);
  }
};

const loadSettingsFromStorage = (): SiteSettings => {
  try {
    const settingsJson = localStorage.getItem(STORAGE_KEYS.SETTINGS);
    if (settingsJson) {
      return JSON.parse(settingsJson);
    }
  } catch (error) {
    console.error('Failed to load settings:', error);
  }
  // Return defaults if no saved settings
  return {
    showTowerLabels: true
  };
};

interface SiteState {
  currentSite: Site | null;
  availableSites: Site[];
  selectedMountingPoint: MountingPoint | null;
  selectedEquipment: TowerEquipment | null;
  hoveredEquipmentId: string | null;
  isEquipmentModalOpen: boolean;
  isSiteListOpen: boolean;
  showTowerLabels: boolean;
  collisions: Record<string, Array<{ withId: string | null; type: 'dish' | 'staywire' }>>;
  
  // Actions
  createSite: (name: string, location: string, towerHeight?: number, crossMemberWidth?: number, sectionHeight?: number, shape?: TowerShape, orientationDeg?: number, stayWires?: StayWire[]) => void;
  loadSite: (siteId: string) => void;
  loadLastSite: () => void;
  updateSite: (siteId: string, updates: Partial<Site>) => void;
  addEquipmentToTower: (equipment: Equipment, mountingPointId: string) => void;
  updateEquipment: (equipmentId: string, updates: Partial<TowerEquipment>) => void;
  moveEquipmentToHeight: (equipmentId: string, targetHeight: number) => void;
  removeEquipment: (equipmentId: string) => void;
  deleteSite: (siteId: string) => void;
  setSelectedMountingPoint: (point: MountingPoint | null) => void;
  setSelectedEquipment: (equipment: TowerEquipment | null) => void;
  setHoveredEquipmentId: (id: string | null) => void;
  setEquipmentModalOpen: (open: boolean) => void;
  setSiteListOpen: (open: boolean) => void;
  setShowTowerLabels: (show: boolean) => void;
  recomputeCollisions: () => void;
  refreshSites: () => void;
  setTowerOrientation: (siteId: string, newDeg: number) => void;
}

export const useSiteStore = create<SiteState>((set, get) => {
  // Load saved settings on initialization
  const savedSettings = loadSettingsFromStorage();
  
  return {
  currentSite: null,
  availableSites: [],
  selectedMountingPoint: null,
  selectedEquipment: null,
  hoveredEquipmentId: null,
  isEquipmentModalOpen: false,
  isSiteListOpen: false,
  showTowerLabels: savedSettings.showTowerLabels,
  collisions: {},

  createSite: (name, location, towerHeight = 30, crossMemberWidth = 450, sectionHeight = 3, shape: TowerShape = 'square', orientationDeg: number = 0, stayWires: StayWire[] = []) => {
    const tower: Tower = {
      id: generateId(),
      height: towerHeight,
      crossMemberWidth,
      sectionHeight,
      shape,
      mountingPoints: applyOrientationToMountingPoints(calculateMountingPoints(towerHeight, shape), norm(orientationDeg)),
      stayWires: applyOrientationToStayWires(stayWires, norm(orientationDeg)),
      orientationDeg: norm(orientationDeg)
    };

    const site: Site = {
      id: generateId(),
      name,
      location,
      tower,
      equipment: [],
      createdAt: new Date(),
      updatedAt: new Date()
    };

    void useSitesStore.getState().createSite(site);
    localStorage.setItem(STORAGE_KEYS.LAST_SITE, site.id);
    set({ currentSite: site });
    get().refreshSites();
    get().recomputeCollisions();
  },

  loadSite: (siteId) => {
    const fromStore = useSitesStore.getState().getSiteById(siteId);
    if (fromStore) {
      set({ currentSite: fromStore });
      get().recomputeCollisions();
      localStorage.setItem(STORAGE_KEYS.LAST_SITE, siteId);
      return;
    }

    const site = loadSiteFromStorage(siteId);
    if (site) {
      set({ currentSite: site });
      get().recomputeCollisions();
    }
  },

  loadLastSite: () => {
    const lastSiteId = getLastSiteId();
    if (lastSiteId) {
      const site = loadSiteFromStorage(lastSiteId);
      if (site) {
        set({ currentSite: site });
        get().recomputeCollisions();
      }
    }
  },

  updateSite: (siteId, updates) => {
    const state = get();
    if (state.currentSite && state.currentSite.id === siteId) {
      let updatedSite = {
        ...state.currentSite,
        ...updates,
        updatedAt: new Date()
      };

      // If tower properties are updated, recalculate mounting points and stay wires
      if (updates.tower) {
        const newTower = { ...updatedSite.tower, ...updates.tower };
        
        // If height changed, recalculate mounting points
        if (updates.tower.height !== state.currentSite.tower.height) {
          const newHeight = updates.tower.height;
          const shape = updates.tower.shape || state.currentSite.tower.shape;
          const newMountingPoints = calculateMountingPoints(newHeight, shape);
          
          // Preserve occupancy from old mounting points where possible
          const occupiedHeightsAndSides = new Set(
            state.currentSite.tower.mountingPoints
              .filter(mp => mp.occupied)
              .map(mp => `${mp.height}-${mp.side}`)
          );
          
          // Mark corresponding new mounting points as occupied
          const updatedMountingPoints = newMountingPoints.map(mp => ({
            ...mp,
            occupied: occupiedHeightsAndSides.has(`${mp.height}-${mp.side}`)
          }));
          
          newTower.mountingPoints = updatedMountingPoints;
        }
        
        // Apply orientation to stay wires if orientation changed
        if (updates.tower.orientationDeg !== undefined) {
          newTower.stayWires = applyOrientationToStayWires(
            newTower.stayWires || [],
            norm(newTower.orientationDeg || 0)
          );
        }
        
        updatedSite = {
          ...updatedSite,
          tower: newTower
        };
      }

      set({ currentSite: updatedSite });
      void persistSite(updatedSite);
      
      get().refreshSites();
      get().recomputeCollisions();
    }
  },

  addEquipmentToTower: (equipment, mountingPointId) => {
    const state = get();
    if (!state.currentSite) return;

    // Find the mounting point to get its angle for correct initial azimuth
    const mountingPoint = state.currentSite.tower.mountingPoints.find(
      mp => mp.id === mountingPointId
    );
    
    if (!mountingPoint) return;

    // Prevent placing dishes on points where stay wires are attached
    const isDish = equipment.type === 'radio-dish';
    if (isDish) {
      const hasStayWireAtPoint = (state.currentSite.tower.stayWires || []).some(w => {
        const sameAngle = Math.abs((w.angle || 0) - (mountingPoint.angle || 0)) < 1e-6;
        const sameHeight = Math.abs((w.height || 0) - (mountingPoint.height || 0)) < 1e-6;
        return sameAngle && sameHeight;
      });
      if (hasStayWireAtPoint) {
        // Silently ignore for now; central guard prevents invalid placement
        return;
      }
    }

    // For triangle towers, side 'A' visually faces opposite; adjust initial azimuth
    let initialAzimuth: number;
    if (mountingPoint.side === 'A') {
      initialAzimuth = 90;
    } else if (mountingPoint.side === 'B') {
      initialAzimuth = 0;
    } else {
      initialAzimuth = mountingPoint.angle;
    }

    const towerEquipment: TowerEquipment = {
      id: generateId(),
      equipment,
      mountingPointId,
      azimuth: initialAzimuth, // Face outward on placement (handle side 'A' adjustment)
      elevation: 0
    };

    // Mark mounting point as occupied
    const updatedMountingPoints = state.currentSite.tower.mountingPoints.map(mp =>
      mp.id === mountingPointId ? { ...mp, occupied: true } : mp
    );

    const updatedSite: Site = {
      ...state.currentSite,
      equipment: [...state.currentSite.equipment, towerEquipment],
      tower: {
        ...state.currentSite.tower,
        mountingPoints: updatedMountingPoints
      },
      updatedAt: new Date()
    };

    set({ currentSite: updatedSite });
    void persistSite(updatedSite);
    
    get().refreshSites();
    set({ selectedMountingPoint: null, isEquipmentModalOpen: false });
    get().recomputeCollisions();
  },

  updateEquipment: (equipmentId, updates) => {
    const state = get();
    if (!state.currentSite) return;

    const updatedEquipment = state.currentSite.equipment.map(eq =>
      eq.id === equipmentId ? { ...eq, ...updates } : eq
    );

    const updatedSite: Site = {
      ...state.currentSite,
      equipment: updatedEquipment,
      updatedAt: new Date()
    };

    set({ currentSite: updatedSite });
    void persistSite(updatedSite);
    
    // Update selected equipment if it's the one being updated
    if (state.selectedEquipment?.id === equipmentId) {
      set({ selectedEquipment: updatedEquipment.find(eq => eq.id === equipmentId) || null });
    }
    
    get().refreshSites();
    get().recomputeCollisions();
  },

  moveEquipmentToHeight: (equipmentId, targetHeight) => {
    const state = get();
    if (!state.currentSite) return;
    const site = state.currentSite;
    const eq = site.equipment.find(e => e.id === equipmentId);
    if (!eq) return;

    // Find current mounting point to preserve side
    const currentMp = site.tower.mountingPoints.find(mp => mp.id === eq.mountingPointId);
    if (!currentMp) return;

    // Clamp target height within tower height
    const clampedTarget = Math.max(0, Math.min(site.tower.height, targetHeight));

    // Find closest mounting point at same side, excluding points blocked by stay wires for dishes
    const isDish = eq.equipment.type === 'radio-dish';
    const candidatesAll = site.tower.mountingPoints.filter(mp => mp.side === currentMp.side);
    const candidates = isDish
      ? candidatesAll.filter(mp => {
          const blocked = (site.tower.stayWires || []).some(w => {
            const sameAngle = Math.abs((w.angle || 0) - (mp.angle || 0)) < 1e-6;
            const sameHeight = Math.abs((w.height || 0) - (mp.height || 0)) < 1e-6;
            return sameAngle && sameHeight;
          });
          return !blocked;
        })
      : candidatesAll;
    if (candidates.length === 0) return;
    let best = candidates[0];
    let bestDiff = Math.abs(candidates[0].height - clampedTarget);
    for (let i = 1; i < candidates.length; i++) {
      const d = Math.abs(candidates[i].height - clampedTarget);
      if (d < bestDiff) {
        best = candidates[i];
        bestDiff = d;
      }
    }

    // If same mounting point, nothing to do
    if (best.id === eq.mountingPointId) return;

    // If best is occupied by another equipment, do nothing (could enhance later)
    const occupiedByOther = site.equipment.some(other => other.id !== equipmentId && other.mountingPointId === best.id);
    if (occupiedByOther) {
      return;
    }

    // Update mounting points occupancy
    const updatedMountingPoints = site.tower.mountingPoints.map(mp => {
      if (mp.id === currentMp.id) {
        return { ...mp, occupied: false };
      }
      if (mp.id === best.id) {
        return { ...mp, occupied: true };
      }
      return mp;
    });

    // Update equipment mountingPointId
    const updatedEquipment = site.equipment.map(e => e.id === equipmentId ? { ...e, mountingPointId: best.id } : e);

    const updatedSite: Site = {
      ...site,
      tower: { ...site.tower, mountingPoints: updatedMountingPoints },
      equipment: updatedEquipment,
      updatedAt: new Date()
    };

    set({ currentSite: updatedSite });
    void persistSite(updatedSite);

    // Update selected equipment reference
    if (state.selectedEquipment?.id === equipmentId) {
      set({ selectedEquipment: updatedEquipment.find(e => e.id === equipmentId) || null });
    }

    get().refreshSites();
    get().recomputeCollisions();
  },

  removeEquipment: (equipmentId) => {
    const state = get();
    if (!state.currentSite) return;

    const equipment = state.currentSite.equipment.find(eq => eq.id === equipmentId);
    if (!equipment) return;

    // Free up mounting point
    const updatedMountingPoints = state.currentSite.tower.mountingPoints.map(mp =>
      mp.id === equipment.mountingPointId ? { ...mp, occupied: false } : mp
    );

    const updatedSite: Site = {
      ...state.currentSite,
      equipment: state.currentSite.equipment.filter(eq => eq.id !== equipmentId),
      tower: {
        ...state.currentSite.tower,
        mountingPoints: updatedMountingPoints
      },
      updatedAt: new Date()
    };

    set({ currentSite: updatedSite });
    void persistSite(updatedSite);
    
    // Clear selection if we just removed the selected equipment
    if (state.selectedEquipment?.id === equipmentId) {
      set({ selectedEquipment: null });
    }
    get().refreshSites();
    get().recomputeCollisions();
  },

  deleteSite: (siteId) => {
    void useSitesStore.getState().deleteSite(siteId);

    const lastSiteId = getLastSiteId();
    if (lastSiteId === siteId) {
      localStorage.removeItem(STORAGE_KEYS.LAST_SITE);
      set({ currentSite: null });
    }

    const state = get();
    if (state.currentSite?.id === siteId) {
      set({ currentSite: null });
    }

    get().refreshSites();
    set({ collisions: {} });
  },

      setTowerOrientation: (siteId, newDeg) => {
        const state = get();
        if (!state.currentSite || state.currentSite.id !== siteId) return;
        const site = state.currentSite;
        const old = site.tower.orientationDeg ?? 0;
        const delta = norm(newDeg - old);

        // Rotate reported equipment azimuths by delta so numeric azimuth follows world heading
        const rotatedEquipment = site.equipment.map(eq => ({
          ...eq,
          azimuth: norm((eq.azimuth || 0) + delta)
        }));

        const updated: Site = {
          ...site,
          tower: { ...site.tower, orientationDeg: norm(newDeg) },
          equipment: rotatedEquipment,
          updatedAt: new Date()
        };

        set({ currentSite: updated });
        void persistSite(updated);
        
        get().refreshSites();
        get().recomputeCollisions();
      },

  setSelectedMountingPoint: (point) => {
    set({ selectedMountingPoint: point });
  },

  setSelectedEquipment: (equipment) => {
    set({ selectedEquipment: equipment });
  },

  setHoveredEquipmentId: (id) => {
    set({ hoveredEquipmentId: id });
  },

  setEquipmentModalOpen: (open) => {
    set({ isEquipmentModalOpen: open });
  },

  setSiteListOpen: (open) => {
    set({ isSiteListOpen: open });
  },

  setShowTowerLabels: (show) => {
    set({ showTowerLabels: show });
    const currentSettings = loadSettingsFromStorage();
    saveSettingsToStorage({ ...currentSettings, showTowerLabels: show });
    patchAndPersistAccountUiPrefs(
      { showTowerLabels: show },
      useAuthStore.getState().user?.id ?? null
    );
  },

  recomputeCollisions: () => {
    const state = get();
    const site = state.currentSite;
    if (!site) {
      set({ collisions: {} });
      return;
    }

    const towerRadius = 0.3;

    // Helper: dish radius mapping (meters) - must match Equipment3D visuals (diameter/2)
    const getDishRadiusMeters = (eq: TowerEquipment): number => {
      const id = (eq.equipment.id || '').toLowerCase();
      const name = (eq.equipment.name || '').toLowerCase();
      if (id.includes('5g34') || name.includes('5g-34') || name.includes('34dbi')) return 1.05 / 2;
      if (id.includes('5g30') || name.includes('5g-30') || name.includes('30dbi')) return 0.65 / 2;
      if (name.includes('23dbi')) return 0.378 / 2;
      return (eq.dishSize || 0.2) / 2;
    };

    // Compute world position of equipment center
    const getEquipmentCenter = (eq: TowerEquipment): { x: number; y: number; z: number; radius: number; thickness: number; type: 'dish' | 'sector' | 'other' } => {
      const mp = site.tower.mountingPoints.find(m => m.id === eq.mountingPointId);
      if (!mp) return { x: 0, y: 0, z: 0, radius: 0.1, thickness: 0.1, type: 'other' };
      const angle = (mp.angle * Math.PI) / 180;
      const offset = eq.offset !== undefined ? eq.offset : 0.1;
      const totalR = towerRadius + offset;
      const y = mp.height;
      if (eq.equipment.type === 'radio-dish') {
        const radius = getDishRadiusMeters(eq);
        const thickness = radius * 0.25;
        // Shift center outward by half thickness to better approximate visual front face
        const radial = totalR + thickness / 2;
        const x = Math.cos(angle) * radial;
        const z = Math.sin(angle) * radial;
        return { x, y, z, radius, thickness, type: 'dish' };
      }
      if (eq.equipment.type === 'sector-antenna') {
        const radial = totalR + 0.05;
        const x = Math.cos(angle) * radial;
        const z = Math.sin(angle) * radial;
        return { x, y, z, radius: 0.15, thickness: 0.1, type: 'sector' };
      }
      const radial = totalR + 0.05;
      const x = Math.cos(angle) * radial;
      const z = Math.sin(angle) * radial;
      return { x, y, z, radius: 0.12, thickness: 0.1, type: 'other' };
    };

    // Distance helpers
    const sqr = (n: number) => n * n;
    const dist2 = (a: any, b: any) => sqr(a.x - b.x) + sqr(a.y - b.y) + sqr(a.z - b.z);
    const pointToSegmentDist2 = (p: any, a: any, b: any) => {
      const ab = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z };
      const ap = { x: p.x - a.x, y: p.y - a.y, z: p.z - a.z };
      const ab2 = sqr(ab.x) + sqr(ab.y) + sqr(ab.z);
      const t = ab2 === 0 ? 0 : Math.max(0, Math.min(1, (ab.x * ap.x + ab.y * ap.y + ab.z * ap.z) / ab2));
      const closest = { x: a.x + ab.x * t, y: a.y + ab.y * t, z: a.z + ab.z * t };
      return dist2(p, closest);
    };

    // Precompute equipment spheres
    const spheres = site.equipment.map(eq => ({ eq, sph: getEquipmentCenter(eq) }));

    const collisions: Record<string, Array<{ withId: string | null; type: 'dish' | 'staywire' }>> = {};

    // Dish-to-dish / sector collisions (approximate)
    const safetyMargin = 0.05; // 5cm margin for near-collision
    for (let i = 0; i < spheres.length; i++) {
      for (let j = i + 1; j < spheres.length; j++) {
        const A = spheres[i];
        const B = spheres[j];
        // Horizontal (plan view) proximity using dish/sector radii
        const dx = A.sph.x - B.sph.x;
        const dz = A.sph.z - B.sph.z;
        const xzDist2 = dx * dx + dz * dz;
        const rSum = A.sph.radius + B.sph.radius + safetyMargin;
        const xzClose = xzDist2 < rSum * rSum;
        // Vertical overlap using thickness
        const yDelta = Math.abs(A.sph.y - B.sph.y);
        const yOverlap = yDelta < (A.sph.thickness / 2 + B.sph.thickness / 2 + safetyMargin);
        // Fallback: full 3D sphere-sphere proximity
        const d3Close = dist2(A.sph, B.sph) < rSum * rSum;
        if ((xzClose && yOverlap) || d3Close) {
          if (!collisions[A.eq.id]) collisions[A.eq.id] = [];
          if (!collisions[B.eq.id]) collisions[B.eq.id] = [];
          collisions[A.eq.id].push({ withId: B.eq.id, type: 'dish' });
          collisions[B.eq.id].push({ withId: A.eq.id, type: 'dish' });
        }
      }
    }

    // Dish/sector to stay-wire collisions (sphere-segment)
    const wires = (site.tower.stayWires || []).map(w => {
      const ang = (w.angle * Math.PI) / 180;
      const a = { x: Math.cos(ang) * towerRadius, y: w.height, z: Math.sin(ang) * towerRadius };
      const b = { x: Math.cos(ang) * w.distance, y: 0, z: Math.sin(ang) * w.distance };
      return { a, b };
    });

    spheres.forEach(({ eq, sph }) => {
      for (const w of wires) {
        const d2 = pointToSegmentDist2(sph, w.a, w.b);
        const r = sph.radius + safetyMargin;
        if (d2 < r * r) {
          if (!collisions[eq.id]) collisions[eq.id] = [];
          collisions[eq.id].push({ withId: null, type: 'staywire' });
          break;
        }
      }
    });

    set({ collisions });
  },

  refreshSites: () => {
    set({ availableSites: useSitesStore.getState().sites });
  }
};});

